import React from "react";
import tw from "tailwind-styled-components";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Config from "../Config";
import { capitalizeFirstLetter } from "../helpers";
import { FaBuilding, FaBuildingUser, FaCrown, FaHandshake, FaMoneyBillTransfer, FaUserGroup, FaUserPlus, FaUsersBetweenLines, FaUsersGear, FaUserShield, FaUserTie, FaUtensils } from "react-icons/fa6";
import {
  FaShieldHalved,
  FaBoxOpen,
  FaHourglassHalf,
  FaGear,
  FaFileLines,
  FaCreditCard,
  FaClockRotateLeft,
  FaChartSimple,
  FaBoxesStacked,
  FaArrowRightFromBracket,
  FaEnvelope,
} from "react-icons/fa6";
import { FaBullhorn } from "react-icons/fa";
import { MdInventory2, MdSubscriptions } from "react-icons/md";

const NavFloatingMenu = ({ setIsUserMenuVisible, logout }) => {
  const user = useSelector((state) => state.UserReducer.user);

  return (
    <Wrapper>
      <Triangle />
      <HeaderSection>
        <AvatarCircle>
          {capitalizeFirstLetter(user?.name?.charAt(0) || "A")}
        </AvatarCircle>
        <div className="flex flex-col">
          <Greeting>
            Hi, {capitalizeFirstLetter(user?.name || "Admin")}
          </Greeting>
        </div>
      </HeaderSection>

      <MenuWrapper>
        <ScrollArea>
          <MenuLinks setIsUserMenuVisible={setIsUserMenuVisible} user={user} />
          {user.userType === Config.userType.SUPERADMIN && (
            <StyledLink to="/mail-brodcaster">
              <MenuItem>
                <Icon>
                  <FaEnvelope />
                </Icon>{" "}
                Mail Broadcaster
              </MenuItem>
            </StyledLink>
          )}
        </ScrollArea>

        <Divider />

        <LogoutButton onClick={logout}>
          <Icon className="text-red-400">
            <FaArrowRightFromBracket />
          </Icon>{" "}
          Log Out
        </LogoutButton>
      </MenuWrapper>
    </Wrapper>
  );
};

const MenuLinks = ({ user }) => {
  const isAgent = user.userType === Config.userType.FIELDAGENT;

  const agentLinks = [
    { to: "/prospects", label: "Prospects", icon: <FaUserGroup /> },
    { to: "/eqaro-users", label: "Eqaro Users", icon: <FaShieldHalved /> },
    { to: "/allproperties", label: "Properties", icon: <FaBuilding /> },
    { to: "/moveout", label: "Move Out", icon: <FaBoxOpen /> },
    {
      to: "/holdtransaction",
      label: "Hold Transaction",
      icon: <FaHourglassHalf />,
    },
    // { to: "/allstaff", label: "Staff", icon: <FaUser />},
    { to: "/systemconfig", label: "System Config", icon: <FaGear /> },
    { to: "/systemlog", label: "System Logs", icon: <FaFileLines /> },
    { to: "/expenses", label: "Expenses", icon: <FaCreditCard /> },
    // { to: "/inventorycategory", label: "Food Category", icon: <FaUtensils /> },
    { to: "/inventorycategory", label: "Inventory Category", icon: <MdInventory2 /> },
    { to: "/recent-used", label: "Recent Used", icon: <FaClockRotateLeft /> },
    { to: "/recent-staff", label: "Recent Staff", icon: <FaUserTie />},
    { to: "/dueslogs", label: "Dues Logs", icon: <FaChartSimple /> },
    { to: "/partner-wise", label: "Partner Wise", icon: <FaHandshake />},
    { to: "/partnerwisetenants", label: "Partner Wise Tenants", icon: <FaUsersBetweenLines />},
    // { to: "/transaction", label: "Transactions", icon: <FaMoneyBillTransfer />},
    { to: "/newowners", label: "Inactive Owners", icon: <FaUserShield />},
    { to: "/latestowners", label: "Latest Owners", icon: <FaBuildingUser   /> },
    { to: "/daywiseanalytics", label: "Client Overview", icon: <FaCrown /> },
    // { to: "/subscription", label: "Subscription", icon: <MdSubscriptions />  },
    { to: "/add-asset", label: "Assets", icon: <FaBoxesStacked /> },
    // { to: "/broadcast", label: "Broadcast Message", icon: <FaBullhorn /> },
    // { to: "/leads", label: "Leads", icon:<FaMagnifyingGlassChart />},
  ];

  return (
    <>
      {isAgent &&
        agentLinks.map((link) => (
          <StyledLink key={link.to} to={link.to}>
            <MenuItem>
              <Icon>{link.icon}</Icon> {link.label}
            </MenuItem>
          </StyledLink>
        ))}

        {user?.userId === 1 && (
          <StyledLink to="/subscription">
            <MenuItem>
              <Icon>
                <FaUsersGear />
              </Icon>
              Subscription
            </MenuItem>
          </StyledLink>
        )}

      <ExternalLink
        href="https://cms.kipinn.com/backend/uploads/docs/RentAgreementTemplateFinal.html"
        target="_blank"
      >
        <Icon>
          <FaFileLines />
        </Icon>{" "}
        Rent Agreement Template
      </ExternalLink>
    </>
  );
};

const Wrapper = tw.div`
  relative
  absolute top-16 right-4 w-72 
  bg-white backdrop-blur-md 
  rounded-2xl border border-gray-100 
  shadow-[0_20px_50px_rgba(0,0,0,0.1)] 
  z-50 overflow-visible
`;

const Triangle = tw.div`
  w-4 h-4 absolute -top-2 right-6
  bg-white rotate-45
  border-l border-t border-gray-200
`;
const HeaderSection = tw.div`
  px-5 py-4 bg-gray-50/50 flex items-center gap-3 border-b border-gray-100
`;

const AvatarCircle = tw.div`
  w-10 h-10 rounded-full bg-primary-color flex items-center justify-center 
  text-white font-bold text-sm shadow-inner
`;

const Greeting = tw.h2`
  text-gray-900 text-sm font-semibold m-0 leading-tight
`;

const MenuWrapper = tw.div`
  p-2 flex flex-col
`;

const ScrollArea = tw.div`
  max-h-[400px] overflow-y-auto px-1
  scrollbar-thin scrollbar-thumb-gray-200
`;

const StyledLink = tw(Link)`
  no-underline block group
`;

const MenuItem = tw.div`
  flex items-center gap-3 px-3 py-2.5 rounded-lg
  text-gray-600 text-[14px] font-medium
  transition-all duration-200
  hover:bg-indigo-50 hover:text-indigo-600
`;

const ExternalLink = tw.a`
  flex items-center gap-3 px-3 py-2.5 rounded-lg
  text-indigo-500 text-[14px] font-medium no-underline
  hover:bg-indigo-50 transition-all
`;

const Icon = tw.span`
  flex items-center justify-center w-8 h-8 
  rounded-lg bg-gray-50 text-slate-400
  group-hover:bg-indigo-50 group-hover:text-indigo-600
  transition-colors duration-200 text-base
`;

const Divider = tw.div`
  h-[1px] bg-gray-100 my-2 mx-3
`;

const LogoutButton = tw.button`
  flex items-center gap-3 px-3 py-3 w-full
  text-red-500 text-[14px] font-semibold text-left
  hover:bg-red-50 rounded-xl transition-all
`;

export default NavFloatingMenu;










































































// import React from "react";
// import tw from "tailwind-styled-components";
// import { Link } from "react-router-dom";
// import { useSelector } from "react-redux";
// import Config from "../Config";

// const NavFloatingMenu = ({ setIsUserMenuVisible, logout }) => {
//   const user = useSelector((state) => state.UserReducer.user);

//   return (
//     <Wrapper>
//       <Triangle></Triangle>

//       <Greeting>Hi, {user?.name || "Admin"}</Greeting>
//       <MenuWrapper>
//         <MenuLinks setIsUserMenuVisible={setIsUserMenuVisible} user={user} />
//         {user.userType == Config.userType.SUPERADMIN && (
//           <Link to="/mail-brodcaster">
//             <MenuItem>Mail Brodcaster</MenuItem>
//           </Link>
//         )}
//         <Logout onClick={logout}>Logout</Logout>
//       </MenuWrapper>
//     </Wrapper>
//   );
// };

// const MenuLinks = ({ setIsUserMenuVisible, user, t }) => (
//   <>
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/prospects">
//         <MenuItem>Prospects</MenuItem>
//       </Link>
//     )}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/eqaro-users">
//         <MenuItem>Eqaro Users</MenuItem>
//       </Link>
//     )}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/allproperties">
//         <MenuItem>Properties</MenuItem>
//       </Link>
//     )}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/moveout">
//         <MenuItem>Move Out</MenuItem>
//       </Link>
//     )}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/holdtransaction">
//         <MenuItem>Hold Transaction</MenuItem>
//       </Link>
//     )}

//     {/* {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/allstaff">
//         <MenuItem>Staffs</MenuItem>
//       </Link>
//     )} */}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/systemconfig">
//         <MenuItem>System Config</MenuItem>
//       </Link>
//     )}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/systemlog">
//         <MenuItem>System Logs</MenuItem>
//       </Link>
//     )}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/expenses">
//         <MenuItem>Expenses</MenuItem>
//       </Link>
//     )}
//     {/* {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/colleges">
//         <MenuItem>Colleges</MenuItem>
//       </Link>
//     )} */}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/recent-used">
//         <MenuItem>Recent Used</MenuItem>
//       </Link>
//     )}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/recent-staff">
//         <MenuItem>Recent Staff</MenuItem>
//       </Link>
//     )}

//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/dueslogs">
//         <MenuItem>Dues Logs</MenuItem>
//       </Link>
//     )}

//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/partner-wise">
//         <MenuItem>Partner Wise</MenuItem>
//       </Link>
//     )}

//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/partnerwisetenants">
//         <MenuItem>Partner Wise Tenants</MenuItem>
//       </Link>
//     )}
//     {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/add-asset">
//         <MenuItem>Assets</MenuItem>
//       </Link>
//     )}
//      {/* {user.userType == Config.userType.FIELDAGENT && (
//       <Link to="/leads">
//         <MenuItem>Leads</MenuItem>
//       </Link>
//     )} */}
//     <a
//       className="text-blue-500 underline"
//       href={`https://cms.kipinn.com/backend/uploads/docs/RentAgreementTemplateFinal.html`}
//       rel="noreferrer"
//       target="_blank"
//     >
//       Rent Agreement Template
//     </a>

//   </>
// );

// const Wrapper = tw.div`
// p-6  rounded-md bg-white border-2 border-gray-100 w-56  absolute top-14 right-0 shadow-lg z-10 lg:top-14 lg:w-64`;

// const Triangle = tw.div`
// w-5 h-5 bg-white absolute -top-3 right-5 transform rotate-45  border-l-2 border-t-2 border-gray-100`;

// const Greeting = tw.h2`
// text-gray-800 text-sm md:text-base mb-5 font-normal truncate`;

// const MenuWrapper = tw.div`
// flex flex-col gap-2 w-full
// max-h-[400px] overflow-y-auto px-1
// scrollbar-thin scrollbar-thumb-gray-200`;

// const MenuItem = tw.p`
// ${(p) => p.$extraClass}
// text-sm w-full relative text-gray-400 border-b border-gray-100 pb-2 hover:text-primary-color  cursor-pointer `;

// const Logout = tw.p`
// text-red-500 text-sm cursor-pointer`;

// export default NavFloatingMenu;
